/*
 * `useDraft` — the composer's side of the never-lose-work layer (spec 08 §4, A19). Typing updates
 * React state at once and the store 400 ms after the last keystroke, with a 5 s ceiling while the
 * typing never pauses; the write is forced out on blur (the composer calls `flush`), on a
 * conversation switch (the key changes), on `visibilitychange → hidden` and on `pagehide`. Worst
 * case after a crash: five seconds of text.
 *
 * Two tabs on the same target converge through `BroadcastChannel("teambase-drafts")`: the focused
 * tab owns the draft, a background tab drops its own pending write rather than clobbering the live
 * one, and takes the written text over (protocol.md §2.12).
 *
 * A surface that keeps settings beside the text — the new AI chat form — writes them with
 * `setFields` and they travel exactly like the text: debounced, flushed, adopted from another tab,
 * restored after a reload.
 */

import {
	createContext,
	createElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
	type ReactNode,
} from "react"
import { DraftStore } from "./draftStore.ts"
import type { AttachmentRef, DraftFields, DraftHandle, DraftRecord, TargetKey } from "./types.ts"

export const DRAFT_DEBOUNCE_MS = 400
export const DRAFT_MAX_WAIT_MS = 5000
export const DRAFTS_CHANNEL = "teambase-drafts"

type ChannelMessage =
	| { type: "typing"; key: TargetKey; from: string }
	| { type: "written"; record: DraftRecord; from: string }

const tabId = crypto.randomUUID()

/** One channel per tab, opened on first use; absent where the platform has none. */
let channel: BroadcastChannel | undefined | null
function draftsChannel(): BroadcastChannel | undefined {
	if (channel === undefined) {
		channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(DRAFTS_CHANNEL)
	}
	return channel ?? undefined
}

function post(message: ChannelMessage): void {
	draftsChannel()?.postMessage(message)
}

/** One empty settings object for every draft that has none, so a render compares equal to the last. */
const NO_FIELDS: DraftFields = Object.freeze({})

/** Two attachment lists that name the same files in the same order, whatever their identity. */
function sameRefs(a: readonly AttachmentRef[], b: readonly AttachmentRef[]): boolean {
	return a.length === b.length && a.every((ref, i) => ref.id === b[i]?.id)
}

const DraftStoreContext = createContext<DraftStore | null>(null)

let defaultStore: DraftStore | undefined

/** The application's one draft store, opened on first use. */
export function appDraftStore(): DraftStore {
	defaultStore ??= new DraftStore()
	return defaultStore
}

/** Hands a store to the tree below — a test's, or the application's. */
export function DraftStoreProvider({
	store,
	children,
}: {
	store: DraftStore
	children: ReactNode
}) {
	return createElement(DraftStoreContext.Provider, { value: store }, children)
}

export function useDraftStore(): DraftStore {
	return useContext(DraftStoreContext) ?? appDraftStore()
}

/** True while the interface should say that drafts do not survive on this device. */
export function useStorageBanner(): boolean {
	const store = useDraftStore()
	const subscribe = useCallback((l: () => void) => store.subscribe(l), [store])
	return useSyncExternalStore(subscribe, () => store.banner())
}

interface Pending {
	key: TargetKey
	value: string
	attachments: AttachmentRef[]
	fields: DraftFields
	dirty: boolean
	debounce: ReturnType<typeof setTimeout> | undefined
	maxWait: ReturnType<typeof setTimeout> | undefined
}

/** What the component renders for one key; replaced whole when the key changes. */
interface View {
	key: TargetKey
	value: string
	attachments: AttachmentRef[]
	fields: DraftFields
	recovered: DraftRecord | null
	ready: boolean
	tooLarge: boolean
}

/** A warm mirror answers at once; a store still opening answers through `hydrate` later. */
function initialView(store: DraftStore, key: TargetKey): View {
	const warm = store.mode() !== "opening"
	const record = warm ? store.get(key) : undefined
	return {
		key,
		value: record?.markdown ?? "",
		attachments: record?.attachments ?? [],
		fields: record?.fields ?? NO_FIELDS,
		recovered: record ?? null,
		ready: warm,
		tooLarge: false,
	}
}

export function useDraft(key: TargetKey): DraftHandle {
	const store = useDraftStore()
	const [view, setView] = useState<View>(() => initialView(store, key))
	// A key change is a different draft: reset during render, the documented way to derive state
	// from a prop, so no frame ever shows the old text under the new key.
	if (view.key !== key) setView(initialView(store, key))
	const pending = useRef<Pending>({
		key,
		value: view.value,
		attachments: view.attachments,
		fields: view.fields,
		dirty: false,
		debounce: undefined,
		maxWait: undefined,
	})

	const cancelTimers = useCallback(() => {
		const p = pending.current
		if (p.debounce !== undefined) clearTimeout(p.debounce)
		if (p.maxWait !== undefined) clearTimeout(p.maxWait)
		p.debounce = undefined
		p.maxWait = undefined
	}, [])

	// The one disk write. Everything that wants the draft on disk now — the timers, blur, the
	// switch, the page going away — goes through here, so "is it dirty" is answered in one place.
	const write = useCallback(async (): Promise<void> => {
		cancelTimers()
		const p = pending.current
		if (!p.dirty) return
		p.dirty = false
		const written = p.key
		const result = await store.put(written, p.value, p.attachments, p.fields)
		setView((v) => (v.key === written ? { ...v, tooLarge: result === "tooLarge" } : v))
		if (result === "stored") {
			const record = store.get(written)
			if (record !== undefined) post({ type: "written", record, from: tabId })
		}
	}, [cancelTimers, store])

	const schedule = useCallback(() => {
		const p = pending.current
		if (p.debounce !== undefined) clearTimeout(p.debounce)
		p.debounce = setTimeout(() => void write(), DRAFT_DEBOUNCE_MS)
		p.maxWait ??= setTimeout(() => void write(), DRAFT_MAX_WAIT_MS)
	}, [write])

	const setValue = useCallback(
		(value: string) => {
			const p = pending.current
			p.value = value
			p.dirty = true
			setView((v) => ({ ...v, value }))
			schedule()
			post({ type: "typing", key: p.key, from: tabId })
		},
		[schedule],
	)

	const setAttachments = useCallback(
		(attachments: AttachmentRef[]) => {
			const p = pending.current
			p.attachments = attachments
			p.dirty = true
			setView((v) => ({ ...v, attachments }))
			schedule()
		},
		[schedule],
	)

	const setFields = useCallback(
		(fields: DraftFields) => {
			const p = pending.current
			p.fields = fields
			p.dirty = true
			setView((v) => ({ ...v, fields }))
			schedule()
		},
		[schedule],
	)

	const clear = useCallback(async () => {
		cancelTimers()
		const p = pending.current
		p.value = ""
		p.attachments = []
		p.fields = NO_FIELDS
		p.dirty = false
		setView((v) => ({ ...v, value: "", attachments: [], fields: NO_FIELDS, tooLarge: false }))
		await store.remove(p.key)
	}, [cancelTimers, store])

	// Per key: the pending write follows the view, the store is asked once it is open, and the
	// cleanup flushes the key being left.
	//
	// The store is always waited for, never only when `initialView` found it still opening. The two
	// run at different moments — the render decides warm or cold, this effect runs after it — and
	// IndexedDB usually finishes opening in between, so a branch on `mode()` here would leave a view
	// that rendered cold with no path back to the record on disk: the draft would be written, kept
	// and never put back into the box after a reload (spec 08 §4, A19).
	useEffect(() => {
		let cancelled = false
		const p = pending.current
		p.key = key
		p.dirty = false
		cancelTimers()
		void store.ready.then(() => {
			if (cancelled) return
			const record = store.get(key)
			// A keystroke that landed before the disk answered wins over the disk.
			const adopt = record !== undefined && !p.dirty
			if (adopt) {
				p.value = record.markdown
				p.attachments = record.attachments
				p.fields = record.fields ?? NO_FIELDS
			}
			setView((v) => {
				if (v.key !== key) return v
				// A view that rendered warm can still be behind a record that landed between its
				// render and this effect — the flush of the surface it replaced writes exactly then —
				// so a warm view adopts as well; only a stale one does not.
				if (v.ready) {
					return adopt && (v.value !== record.markdown || v.fields !== (record.fields ?? NO_FIELDS))
						? {
								...v,
								recovered: record,
								value: record.markdown,
								attachments: record.attachments,
								fields: record.fields ?? NO_FIELDS,
							}
						: v
				}
				return {
					...v,
					ready: true,
					recovered: record ?? null,
					...(adopt
						? {
								value: record.markdown,
								attachments: record.attachments,
								fields: record.fields ?? NO_FIELDS,
							}
						: {}),
				}
			})
		})
		return () => {
			cancelled = true
			void write()
		}
	}, [key, store, cancelTimers, write])

	/*
	 * A write into the store that did not come from this hook: the outbox putting a refused or a
	 * withdrawn message's text back where it was typed (`Outbox.restoreDraft`). The draft is the
	 * authority, so the box follows it — this is what makes "Undo" and a refusal put the text back
	 * on screen and not only on disk (P28's missing half).
	 *
	 * A pending keystroke wins: while `dirty` is set, what is in the box is newer than what is in
	 * the store, and adopting would delete somebody's sentence. This hook's own writes are the
	 * no-op case — they clear `dirty` before writing exactly what the view already shows.
	 */
	useEffect(
		() =>
			store.subscribe(() => {
				const p = pending.current
				if (p.dirty) return
				const record = store.get(p.key)
				const value = record?.markdown ?? ""
				const attachments = record?.attachments ?? []
				const fields = record?.fields ?? NO_FIELDS
				if (value === p.value && sameRefs(attachments, p.attachments) && fields === p.fields) {
					return
				}
				p.value = value
				p.attachments = attachments
				p.fields = fields
				setView((v) => (v.key === p.key ? { ...v, value, attachments, fields } : v))
			}),
		[store],
	)

	// The page going away, in both spellings the platforms use.
	useEffect(() => {
		const onHidden = () => {
			if (document.visibilityState === "hidden") void write()
		}
		const onPageHide = () => void write()
		document.addEventListener("visibilitychange", onHidden)
		window.addEventListener("pagehide", onPageHide)
		return () => {
			document.removeEventListener("visibilitychange", onHidden)
			window.removeEventListener("pagehide", onPageHide)
		}
	}, [write])

	// The other tabs. A message for another key or from this tab is nobody's business here.
	useEffect(() => {
		const ch = draftsChannel()
		if (ch === undefined) return
		const onMessage = (event: MessageEvent<ChannelMessage>) => {
			const m = event.data
			const p = pending.current
			if (m.from === tabId || document.hasFocus()) return
			if (m.type === "typing" && m.key === p.key) {
				cancelTimers()
				p.dirty = false
			} else if (m.type === "written" && m.record.key === p.key) {
				cancelTimers()
				p.dirty = false
				p.value = m.record.markdown
				p.attachments = m.record.attachments
				p.fields = m.record.fields ?? NO_FIELDS
				store.adopt(m.record)
				const { markdown, attachments } = m.record
				const fields = p.fields
				setView((v) =>
					v.key === m.record.key ? { ...v, value: markdown, attachments, fields } : v,
				)
			}
		}
		ch.addEventListener("message", onMessage)
		return () => ch.removeEventListener("message", onMessage)
	}, [cancelTimers, store])

	return useMemo(
		() => ({
			value: view.value,
			setValue,
			attachments: view.attachments,
			setAttachments,
			fields: view.fields,
			setFields,
			flush: write,
			clear,
			recovered: view.recovered,
			ready: view.ready,
			tooLarge: view.tooLarge,
		}),
		[view, setValue, setAttachments, setFields, write, clear],
	)
}
